import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../software/api";
import { toast } from "sonner";
import { Plus, Store, Printer, Sparkles, CheckSquare, CloudRain } from "lucide-react";
import RainDayPopup from "./RainDayPopup";
import { BoardBulkBar } from "./BoardBulkBar";
import SuggestDayPopup from "./SuggestDayPopup";
import { ViewSwitcher } from "./ViewSwitcher";
import { MonthGrid } from "./CalendarViews";
import { BoardMagnet } from "./BoardMagnet";
import { MagnetPopup } from "./MagnetPopup";
import { OfficeListPopup } from "./OfficeListPopup";
import { RotaPopover } from "./RotaPopover";
import { KINDS, todayISO, dayLabel, isSaturday, firstName, sameName, rangeFor } from "./boardUtils";

export default function WhiteBoard({ onOpenClient, onAddAway, reloadKey, onPrintRun }) {
  const [view, setView] = useState("fortnight");
  const [anchor, setAnchor] = useState(todayISO());
  const [who, setWho] = useState("all");
  const range = rangeFor(view, anchor);
  const [data, setData] = useState(null);
  const [openMag, setOpenMag] = useState(null);
  const [officeList, setOfficeList] = useState(null);
  const [rotaFor, setRotaFor] = useState(null);
  const [hover, setHover] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [suggest, setSuggest] = useState(false);
  const [rain, setRain] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [sel, setSel] = useState(new Set());
  const clickMag = (m) => {
    if (!selectMode || m.finished) { setOpenMag(m); return; }
    setSel((p) => { const n = new Set(p); n.has(m.id) ? n.delete(m.id) : n.add(m.id); return n; });
  };
  const exitSelect = () => { setSelectMode(false); setSel(new Set()); };
  const today = todayISO();

  const load = useCallback(async () => {
    try { setData(await api("GET", `/board?start=${range.start}&days=${range.days}&today=${today}&history=true`)); } catch (e) { toast.error(e.message); }
  }, [range.start, range.days, today]);
  useEffect(() => { load(); }, [load, reloadKey]);
  // live board: quietly reload every 5s so tech changes from Pool-Tech show up (skipped mid-drag or when hidden)
  const dragRef = React.useRef(false);
  dragRef.current = hover !== null;
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState !== "visible" || dragRef.current) return;
      api("GET", `/board?start=${range.start}&days=${range.days}&today=${today}&history=true`).then(setData).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [range.start, range.days, today]);

  const rows = useMemo(() => (data ? [...data.staff, ""] : []), [data]);
  const cell = useMemo(() => {
    const map = {};
    const put = (who, d, key, item) => { const k = `${who}|${d}`; (map[k] ||= { mags: [], office: [] })[key].push(item); };
    if (!data) return map;
    const rowOf = (n) => data.staff.find((s) => sameName(s, n)) || "";
    data.magnets.forEach((m) => put(rowOf(m.assigned_to), m.due_date < today && !m.finished ? "overdue" : m.due_date, "mags", m));
    data.office.forEach((o) => put(rowOf(o.assigned_to), o.due_date < today ? "overdue" : o.due_date, "office", o));
    return map;
  }, [data, today]);

  const moveMany = async (ids, whoTo, d) => {
    if (new Date(d + "T00:00:00").getDay() === 0) { toast.error("Nothing is booked on a Sunday — pick another day"); return; }
    const mags = ids.map((id) => data.magnets.find((x) => x.id === id)).filter((m) => m && !m.finished)
      .map((m) => ({ m, who: whoTo === undefined ? (m.assigned_to || "") : whoTo }))
      .filter(({ m, who }) => !(m.due_date === d && sameName(m.assigned_to, who)));
    if (!mags.length) return;
    await Promise.all(mags.map(({ m, who }) => api("PATCH", `/reminders/${m.id}?due_date=${d}&assigned_to=${encodeURIComponent(who)}${m.kind === "away" ? "" : `&on_board=true&kind=${m.kind}`}`)));
    const whoLbl = whoTo === undefined ? "" : `${firstName(whoTo)} · `;
    toast.success(mags.length === 1 ? `Moved to ${whoLbl || firstName(mags[0].who) + " · "}${dayLabel(d)}` : `Moved ${mags.length} magnets to ${whoLbl}${dayLabel(d)}`);
    if (mags.length > 1) exitSelect();
    load();
  };
  const move = (id, whoTo, d) => moveMany(sel.has(id) && sel.size > 1 ? [...sel] : [id], whoTo, d);
  // drop a magnet onto another in the same day: it takes that place; the order is the tech's Pool-Tech run order
  const reorder = async (mags, dragId, targetId) => {
    if (dragId === targetId) return;
    const jobs = mags.filter((x) => x.kind !== "away" && !x.finished), drag = jobs.find((x) => x.id === dragId);
    const rest = jobs.filter((x) => x.id !== dragId), at = rest.findIndex((x) => x.id === targetId);
    const from = jobs.indexOf(drag), to = jobs.findIndex((x) => x.id === targetId);
    const order = [...rest.slice(0, at + (from < to ? 1 : 0)), drag, ...rest.slice(at + (from < to ? 1 : 0))];
    try { await Promise.all(order.map((x, i) => api("PATCH", `/reminders/${x.id}?seq=${i + 1}`))); toast.success("Run order updated"); }
    catch (e) { toast.error(e.message); }
    load();
  };

  if (!data) return <div className="p-10 text-center text-slate-400" data-testid="whiteboard-loading">Loading board…</div>;
  const cols = ["overdue", ...data.dates];

  return (
    <div data-testid="whiteboard">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <ViewSwitcher view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} label={range.label} />
        {view === "month" && (
          <select value={who} onChange={(e) => setWho(e.target.value)} data-testid="board-month-staff" className="rounded-full border-2 border-slate-200 bg-white px-3 py-1 text-xs font-bold">
            <option value="all">All staff</option>
            {data.staff.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {Object.entries(KINDS).map(([k, v]) => <span key={k} className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ background: v.bg, color: v.color }}>{v.label}</span>)}
          <button onClick={() => (selectMode ? exitSelect() : setSelectMode(true))} data-testid="board-select-mode-btn"
            className={`rounded-full text-sm font-bold px-4 py-1.5 inline-flex items-center gap-1 border-2 ${selectMode ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:border-slate-500"}`}><CheckSquare className="h-4 w-4" />{selectMode ? "Done selecting" : "Select"}</button>
          <button onClick={() => setSuggest(true)} data-testid="board-suggest-day-btn" className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-bold px-4 py-1.5 inline-flex items-center gap-1"><Sparkles className="h-4 w-4" />Suggest a Day</button>
          <button onClick={onAddAway} data-testid="board-add-away-btn" className="rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-bold px-4 py-1.5 inline-flex items-center gap-1"><Plus className="h-4 w-4" />Away / Out</button>
        </div>
      </div>

      {selectMode && <BoardBulkBar count={sel.size} staff={data.staff} onClear={exitSelect} onMove={(w, d) => moveMany([...sel], w, d)} />}
      {view === "month" ? (
        <MonthGrid dates={data.dates} month={range.month} today={today} showWho onOpen={clickMag} selectMode={selectMode} selectedIds={sel}
          magnets={data.magnets.filter((m) => who === "all" || sameName(m.assigned_to, who))}
          onMove={(id, d) => move(id, undefined, d)} />
      ) : (
      <div className="overflow-x-auto rounded-xl border-2 border-slate-300 bg-white shadow-inner">
        <table className={`border-collapse text-left w-full table-fixed ${view === "fortnight" ? "min-w-[1400px]" : view === "week" ? "min-w-[900px]" : ""}`} data-testid="board-grid">
          <thead>
            <tr>
              <th className="sticky left-0 z-20 bg-slate-800 text-white text-xs font-bold p-2 w-[92px]">Staff</th>
              {cols.map((d) => {
                const sat = d !== "overdue" && isSaturday(d);
                return (
                  <th key={d} data-testid={`board-col-${d}`} className={`relative p-1.5 text-[11px] font-black uppercase tracking-wide border-l border-slate-600 ${d === "overdue" ? "bg-red-700 text-white w-[130px]" : d === today ? "bg-amber-400 text-slate-900" : "bg-slate-800 text-white"} ${d !== "overdue" && new Date(d + "T00:00:00").getDay() === 1 && d !== data.dates[0] ? "border-l-4 border-l-amber-400" : ""}`}>
                    {d === "overdue" ? "Overdue" : dayLabel(d)}
                    {d === "overdue" && data.older_overdue > 0 && <span className="block text-[9px] font-bold normal-case opacity-80" data-testid="board-older-overdue" title="Reminders overdue by more than a year — find them in the List view">+{data.older_overdue} older than 1 yr (in List)</span>}
                    {sat && (
                      <button onClick={() => setRotaFor(d)} data-testid={`board-rota-${d}`} className="mt-0.5 block w-full rounded bg-white/15 hover:bg-white/30 px-1 py-0.5 text-[9px] font-bold normal-case truncate">
                        <Store className="inline h-3 w-3 -mt-0.5 mr-0.5" />Shop: {(data.rota[d] || []).map(firstName).join(" & ") || "—"}
                      </button>
                    )}
                    {rotaFor === d && <RotaPopover date={d} current={data.rota[d] || []} staff={data.staff} onClose={() => setRotaFor(null)} onSaved={() => { setRotaFor(null); load(); }} />}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((who) => (
              <tr key={who || "everyone"} className="border-t border-slate-200 align-top" data-testid={`board-row-${firstName(who).toLowerCase()}`}>
                <td className="sticky left-0 z-10 bg-slate-100 p-2 font-display font-black text-[13px] text-slate-800 border-r-2 border-slate-300">
                  {firstName(who)}
                  <button onClick={() => onPrintRun(who)} disabled={!who} title="Print today's run sheet" data-testid={`board-print-${firstName(who).toLowerCase()}`} className="block mt-1 text-slate-400 hover:text-slate-800 disabled:hidden"><Printer className="h-3.5 w-3.5" /></button>
                </td>
                {cols.map((d) => {
                  const c = cell[`${who}|${d}`] || { mags: [], office: [] };
                  const shop = d !== "overdue" && isSaturday(d) && who && (data.rota[d] || []).some((n) => sameName(n, who));
                  const key = `${who}|${d}`;
                  const canDrop = d !== "overdue" && !isSaturday(d);
                  return (
                    <td key={d}
                      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setHover(key); } }}
                      onDragLeave={() => setHover(null)}
                      onDrop={(e) => { e.preventDefault(); setHover(null); if (canDrop) move(e.dataTransfer.getData("text/plain"), who, d); }}
                      data-testid={`board-cell-${firstName(who).toLowerCase()}-${d}`}
                      className={`p-1 border-l border-slate-200 min-h-[64px] h-[64px] transition-colors ${hover === key ? "bg-amber-100" : d === "overdue" ? "bg-red-50/60" : d === today ? "bg-amber-50/60" : shop ? "bg-slate-200/70" : ""}`}>
                      <div className="space-y-1">
                        {d !== "overdue" && c.mags.some((m) => m.kind !== "away") && (
                          <div className="flex items-start gap-1">
                            <div className="flex-1 min-w-0 text-[9px] font-bold text-slate-500 leading-tight truncate" data-testid={`board-suburbs-${firstName(who).toLowerCase()}-${d}`}
                              title={[...new Set(c.mags.filter((m) => m.kind !== "away").map((m) => (m.client_address || "").split(",")[1]?.trim()).filter(Boolean))].join(" · ")}>
                              {[...new Set(c.mags.filter((m) => m.kind !== "away").map((m) => (m.client_address || "").split(",")[1]?.trim()).filter(Boolean))].map((x) => x.toLowerCase().replace(/\b\w/g, (ch) => ch.toUpperCase())).join(" · ")}
                            </div>
                            {who && (
                              <button onClick={() => setRain({ tech: who, date: d })} title="Rain day — push this day's jobs to the next working day"
                                data-testid={`board-rain-${firstName(who).toLowerCase()}-${d}`}
                                className="shrink-0 rounded p-0.5 text-sky-700/60 hover:text-sky-800 hover:bg-sky-100"><CloudRain className="h-3.5 w-3.5" /></button>
                            )}
                          </div>
                        )}
                        {shop && <div className="text-[9px] font-black tracking-widest text-slate-500 text-center rounded bg-slate-300/70 py-0.5" data-testid="board-shop-marker">SHOP / COUNTER</div>}
                        {c.office.length > 0 && (
                          <button onClick={() => setOfficeList({ title: `${firstName(who)} · ${d === "overdue" ? "Overdue" : dayLabel(d)} · office reminders`, items: c.office })}
                            data-testid={`board-office-count-${firstName(who).toLowerCase()}-${d}`}
                            className={`w-full rounded-full text-[10px] font-bold px-2 py-0.5 border ${d === "overdue" ? "bg-red-100 border-red-300 text-red-700" : "bg-slate-50 border-slate-300 text-slate-600"} hover:brightness-95`}>
                            {c.office.length} office
                          </button>
                        )}
                        {(d === "overdue" && !expanded[key] ? c.mags.slice(0, 3) : c.mags).map((m) => {
                          const runNo = d !== "overdue" && m.kind !== "away" && !m.finished ? c.mags.filter((x) => x.kind !== "away" && !x.finished).indexOf(m) + 1 : 0;
                          return (
                            <div key={m.id} className="relative" data-testid={`board-slot-${m.id}`}
                              onDrop={(e) => { const id = e.dataTransfer.getData("text/plain"); if (d !== "overdue" && m.kind !== "away" && !m.finished && c.mags.some((x) => x.id === id && x.kind !== "away")) { e.preventDefault(); e.stopPropagation(); setHover(null); reorder(c.mags, id, m.id); } }}>
                              <BoardMagnet m={m} overdue={d === "overdue"} onClick={clickMag} selectMode={selectMode} selected={sel.has(m.id)} />
                              {runNo > 0 && <span data-testid={`board-run-no-${m.id}`} className="pointer-events-none absolute -top-1 -left-1 h-4 min-w-[16px] px-0.5 rounded-full bg-slate-900 text-white text-[9px] font-black leading-4 text-center shadow">{runNo}</span>}
                            </div>
                          );
                        })}
                        {d === "overdue" && c.mags.length > 3 && (
                          <button onClick={() => setExpanded((p) => ({ ...p, [key]: !p[key] }))} data-testid={`board-overdue-more-${firstName(who).toLowerCase()}`}
                            className="w-full text-[10px] font-bold text-red-700 hover:underline">{expanded[key] ? "Show less" : `+${c.mags.length - 3} more overdue jobs`}</button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">Drag a magnet to another day to reschedule, or to another row to reassign. Sundays are never booked.</p>

      {rain && <RainDayPopup {...rain} onClose={() => setRain(null)} onDone={() => { setRain(null); load(); }} />}
      {suggest && <SuggestDayPopup onClose={() => setSuggest(false)} onBooked={() => { setSuggest(false); load(); }} />}
      {openMag && <MagnetPopup m={openMag} onClose={() => setOpenMag(null)} onChanged={() => { setOpenMag(null); load(); }} onOpenClient={onOpenClient} />}
      {officeList && <OfficeListPopup {...officeList} onClose={() => setOfficeList(null)} onChanged={() => { setOfficeList(null); load(); }} onOpenClient={onOpenClient} />}
    </div>
  );
}
